import { useState } from "react";

import {
  Avatar,
  Box,
  Button,
  ButtonBase,
  Grid,
  IconButton,
  StyledEngineProvider,
  Tooltip,
  Typography,
  useTheme,
  ThemeProvider
} from "@mui/material";

import DoneIcon from "@mui/icons-material/Done";
import ShuffleIcon from "@mui/icons-material/Shuffle";
import ScheduleIcon from "@mui/icons-material/Schedule";
import SendIcon from "@mui/icons-material/Send";

import { Backdrop, DefaultPaper, TextField } from "@aclymatepackages/atoms";
import mainTheme, { mergeDarkTheme } from "@aclymatepackages/themes";
import {
  editRowData,
  addDataRow,
  removeDataRow
} from "@aclymatepackages/array-immutability-helpers";
import { hexToRgba } from "@aclymatepackages/converters";
import { emailRegExpTest, numbersRegExpTest } from "@aclymatepackages/reg-exp";
import { letterSBoolean } from "@aclymatepackages/formatters";
import { STARTER_TIER_MAXIMUM_NUMBER_EMPLOYEES } from "@aclymatepackages/constants";

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

import CopyButtonCard from "../../atoms/CopyButtonCard";
import SurveyLanguageToggle from "../../atoms/SurveyLanguageToggle";
import { useNewEmployeeForm } from "../../hooks/employees";
import useCsvUploader from "../../hooks/csvUploader";
import RemovableRow from "../../modules/RemovableRow";
import MultiPartFormLayout from "../flows/MultipartForm";
import ScheduleSurveyModal from "./ScheduleSurveyModal";
import { useEmployeeBulkImportUploader } from "./useEmployeeBulkImportUploader";

import { SurveyLanguageProvider } from "../../../helpers/contexts/surveyLanguage";

import usePlatformLayoutContext from "../../../helpers/contexts/platformLayout";
import {
  useSharedFormLoading,
  doesFieldExist,
  createErrorObj
} from "../../../helpers/components/inputs";
import {
  useCachedFirebaseCrud,
  useAccountData,
  useCachedDisplayData
} from "../../../helpers/firebase";
import useInputType from "../../../helpers/components/display-lists/inputTypes";
import { generateRandomPin } from "../../../helpers/otherHelpers";
import { getAccountId } from "../../../helpers/accountHelpers";
import {
  parseStateFromAddress,
  parseZipCodeFromAddress
} from "../../../helpers/utils/geography";
import { fetchOurApi } from "../../../helpers/utils/apiCalls";
import { analyticsTrack } from "../../../helpers/analytics";
import useAccountingData from "../../../helpers/hooks/accountingData";
import { useStarterTierSubscriptionFlags } from "../../../helpers/hooks/companyData";
import { scheduleSurveyDelivery } from "../../../helpers/components/employees";

dayjs.extend(utc);
dayjs.extend(timezone);

const fieldOptions = [
  { label: "First Name", value: "firstName" },
  { label: "Last Name", value: "lastName" },
  { label: "Name", value: "fullName" },
  { label: "Email Address", value: "email" }
];

const knownFields = [
  { header: "first name", field: "firstName" },
  { header: "last name", field: "lastName" },
  { header: "email address", field: "email" }
];

const calcDescriptionObj = ({ fullAddress, street, city, state, zipCode }) => {
  if (fullAddress) {
    return { description: fullAddress };
  }

  if (street || city || state || zipCode) {
    const buildAddressDescription = (parts) => {
      return parts.filter(Boolean).join(" ");
    };

    const addressParts = [street, city, state && `${state},`, zipCode];

    return { description: buildAddressDescription(addressParts) };
  }

  return {};
};

const buildAddressObj = (address) => {
  const { fullAddress, street, city, state, zipCode } = address;

  if (!fullAddress && !street && !city && !state && !zipCode) {
    return {};
  }

  const getZipCodeObject = () => {
    if (fullAddress) {
      return { zipCode: parseZipCodeFromAddress(fullAddress) };
    }
    if (zipCode) {
      return { zipCode };
    }
    return {};
  };

  const zipObj = getZipCodeObject();

  const descriptionObj = calcDescriptionObj(address);

  const getStateObj = () => {
    if (state) {
      return { state };
    }
    if (!fullAddress) {
      return {};
    }
    const parsedState = parseStateFromAddress(fullAddress);
    return parsedState ? { state: parsedState } : {};
  };

  const stateObj = getStateObj();

  return {
    address: {
      ...zipObj,
      ...descriptionObj,
      ...stateObj
    }
  };
};

const formatEmployeeName = ({ firstName, lastName, fullName, firstLast }) => {
  const matchFirstLast = (name = "") => {
    const firstLastMatch = name.match(/^[a-zA-Z]+,\s?[a-zA-Z]+$/);
    if (!firstLastMatch) {
      return name;
    }

    const [lastName, firstName, ...otherNames] = name
      .split(",")
      .map((name) => name.trim());

    return `${firstName} ${
      otherNames?.length ? otherNames.join(" ") : ""
    } ${lastName}`;
  };

  if (fullName) {
    return matchFirstLast(fullName);
  }

  if (firstName && lastName) {
    return `${firstName.trim()} ${lastName.trim()}`;
  }

  return matchFirstLast(firstLast);
};

const formatScheduledTimeForDisplay = (
  scheduledTime,
  userTimezone = "America/Los_Angeles"
) => {
  const scheduledMoment = dayjs(scheduledTime).tz(userTimezone);
  return scheduledMoment.format("MMMM D, YYYY [at] h:mm A");
};

const useProcessEmployeeInvites = () => {
  const [existingEmployees] = useCachedDisplayData("employees");
  const { setFormLoading } = useSharedFormLoading();
  const { newCollectionDoc } = useCachedFirebaseCrud();
  const [{ name: companyName }] = useAccountData();
  const { willEmployeeLimitBeReachedFromNewEmployees } =
    useStarterTierSubscriptionFlags();

  return ({
      onClose,
      submitErrorMsg = null,
      triggerSnackbar,
      isScheduled = false,
      scheduledTime = null,
      timezone = null
    }) =>
    async ({ data: employees }) => {
      setFormLoading(true);
      const id = getAccountId();

      const filteredEmployees = employees.filter(({ email }) => email);

      if (willEmployeeLimitBeReachedFromNewEmployees(filteredEmployees)) {
        triggerSnackbar(`Adding ${filteredEmployees.length} employees will bring you above the 
          ${STARTER_TIER_MAXIMUM_NUMBER_EMPLOYEES} employee limit. Reduce
          the number of employees to upload in your CSV or upgrade your subscription.
        `);
        onClose();

        return setFormLoading(false);
      }

      const formattedEmployees = filteredEmployees.map((employee) => {
        const { email, startDate } = employee;

        const existingEmployee = existingEmployees.find(
          (existingEmployee) => existingEmployee.email === email
        );

        const existingEmployeeObj = existingEmployee?.length
          ? { existing: true }
          : {};

        const addressObj = buildAddressObj(employee);

        return {
          dateCreated: new Date(),
          origin: "employee",
          name: formatEmployeeName(employee),
          ...addressObj,
          ...existingEmployeeObj,
          email,
          currentLinkedIds: [id],
          links: [
            {
              id,
              name: companyName,
              type: "company",
              startDate,
              status: "unconfirmed",
              employeeStatus: "unconfirmed"
            }
          ]
        };
      });

      const duplicateEmployees = formattedEmployees.filter(
        ({ existing }) => existing
      );

      if (duplicateEmployees.length) {
        const isMultipleEmployees = duplicateEmployees.length > 1;

        const buildDuplicateMessage = (count, isMultiple) => {
          const employeePlural = letterSBoolean({ length: count });
          const wasWere = isMultiple ? "weren't" : "wasn't";
          const aAn = isMultiple ? "" : "a";
          const anAn = isMultiple ? "" : "an";
          const duplicatePlural = letterSBoolean({ length: count });
          const existingPlural = letterSBoolean({ length: count });

          const thoseThat = isMultiple ? "those" : "that";
          const areIs = isMultiple ? "are" : "is";
          const reminderArticle = isMultiple ? "" : "a";

          return `${count} employee${employeePlural} ${wasWere} added because they're ${aAn} duplicate${duplicatePlural} of ${anAn} existing employee${existingPlural}. If ${thoseThat} employee${employeePlural} ${areIs} still unconfirmed, send them ${reminderArticle} reminder${employeePlural} to fill out their survey${employeePlural}.`;
        };

        triggerSnackbar(
          buildDuplicateMessage(duplicateEmployees.length, isMultipleEmployees)
        );
      }

      const newEmployees = formattedEmployees.filter(
        ({ existing }) => !existing
      );
      analyticsTrack("Employees Invited", {
        numberOfEmployees: newEmployees.length,
        isScheduled
      });

      if (isScheduled && scheduledTime) {
        try {
          const createdEmployees = await Promise.all(
            newEmployees.map(async (employee) => {
              const { name } = employee;
              const slug = await fetchOurApi({
                path: "/onboarding/new-document-slug",
                method: "POST",
                data: { name, collection: "individuals" },
                callback: ({ slug }) => slug
              });

              const employeeId = await newCollectionDoc("employees", {
                ...employee,
                slug
              });

              return { ...employee, id: employeeId };
            })
          );

          const response = await scheduleSurveyDelivery({
            employeeData: createdEmployees,
            scheduledTime,
            timezone: timezone || "America/Los_Angeles"
          });

          if (response.success) {
            const formattedTime = formatScheduledTimeForDisplay(
              scheduledTime,
              timezone || "America/Los_Angeles"
            );

            triggerSnackbar(
              `Survey delivery scheduled successfully! ${
                newEmployees.length
              } employee${letterSBoolean(
                newEmployees
              )} will receive the survey on ${formattedTime}.`,
              "success"
            );

            if (response.scheduledJobId) {
              console.log(`Scheduled job ID: ${response.scheduledJobId}`);
            }
          }

          onClose();
          return setFormLoading(false);
        } catch (error) {
          console.error("Error scheduling survey delivery:", error);
          submitErrorMsg &&
            submitErrorMsg(
              error.message ||
                "An error occurred while scheduling the survey delivery. Please try again."
            );
          return setFormLoading(false);
        }
      }

      await Promise.all(
        newEmployees.map(async (employee) => {
          const { name } = employee;
          const slug = await fetchOurApi({
            path: "/onboarding/new-document-slug",
            method: "POST",
            data: { name, collection: "individuals" },
            callback: ({ slug }) => slug
          });

          const employeeId = await newCollectionDoc("employees", {
            ...employee,
            slug
          });

          return await fetchOurApi({
            path: "/survey-emails/employee/invite",
            method: "POST",
            data: {
              employee: { ...employee, id: employeeId },
              companyId: id
            }
          });
        })
      )
        .then(() => onClose())
        .catch(
          () =>
            submitErrorMsg &&
            submitErrorMsg(
              "An error occurred while sending this email. Please try again."
            )
        );

      return setFormLoading(false);
    };
};

const EditableRow = ({
  firstName,
  lastName,
  email,
  editEmployee,
  submitErrorMsg
}) => (
  <>
    <Grid item sm={3}>
      <TextField
        label="First Name"
        value={firstName}
        setValue={(value) => editEmployee("firstName", value)}
        error={!!submitErrorMsg && !firstName}
      />
    </Grid>
    <Grid item sm={4}>
      <TextField
        label="Last Name"
        value={lastName}
        setValue={(value) => editEmployee("lastName", value)}
        error={!!submitErrorMsg && !lastName}
      />
    </Grid>
    <Grid item sm={4}>
      <TextField
        label="Email Address"
        value={email}
        setValue={(value) => editEmployee("email", value)}
        error={!!submitErrorMsg && (!email || emailRegExpTest(email))}
      />
    </Grid>
  </>
);

const ManualEmployeeEmailsRow = ({
  employee: { firstName, lastName, email },
  employeeCount,
  editEmployee,
  removeEmployee,
  submitErrorMsg,
  idx
}) => (
  <>
    {employeeCount > 1 ? (
      <RemovableRow
        rowCount={employeeCount}
        removeRow={removeEmployee}
        idx={idx}
      >
        <EditableRow
          firstName={firstName}
          lastName={lastName}
          email={email}
          editEmployee={editEmployee}
          submitErrorMsg={submitErrorMsg}
        />
      </RemovableRow>
    ) : (
      <Grid container justifyContent="center" spacing={2}>
        <EditableRow
          firstName={firstName}
          lastName={lastName}
          email={email}
          editEmployee={editEmployee}
          submitErrorMsg={submitErrorMsg}
        />
      </Grid>
    )}
  </>
);

const useManualEmployeeEmails = ({ onClose }) => {
  const { activateSnackbar } = usePlatformLayoutContext();

  const processEmployeeInvites = useProcessEmployeeInvites();
  const { willEmployeeLimitBeReachedFromNewEmployees } =
    useStarterTierSubscriptionFlags();

  const [newEmployees, setNewEmployees] = useState([{}]);
  const [submitErrorMsg, setSubmitErrorMsg] = useState("");
  const [showScheduleModal, setShowScheduleModal] = useState(false);

  const isStarterTierEmployeeLimitReached =
    willEmployeeLimitBeReachedFromNewEmployees(newEmployees);

  const triggerSnackbar = (snackbarMessage, type = "warning") =>
    activateSnackbar({ message: snackbarMessage, alert: type });

  const areEmployeesValid = () => {
    const allFieldsFilled = newEmployees.reduce(
      (acc, { firstName, lastName, email }) =>
        acc && firstName && lastName && email && emailRegExpTest(email),
      true
    );

    const duplicateEmployees = newEmployees
      .map((employee, idx, employees) => {
        const { email } = employee;
        if (!email) {
          return employee;
        }
        const otherEmployees = employees.filter((_, i) => i !== idx);
        const duplicateEmployeeEmail = otherEmployees.find(
          (employee) => employee.email === email
        );
        if (duplicateEmployeeEmail) {
          return { ...employee, duplicate: true };
        }
        return employee;
      })
      .filter(({ duplicate }) => duplicate);

    if (duplicateEmployees.length) {
      return false;
    }
    if (!allFieldsFilled) {
      return false;
    }
    return true;
  };

  const submitEmployees = async () =>
    await processEmployeeInvites({
      onClose,
      submitErrorMsg,
      triggerSnackbar
    })({ data: newEmployees });

  const handleScheduleSurvey = async ({
    scheduledTime,
    timezone,
    employeeData
  }) => {
    await processEmployeeInvites({
      onClose,
      triggerSnackbar,
      isScheduled: true,
      scheduledTime,
      timezone
    })({ data: employeeData });

    setShowScheduleModal(false);
  };

  return [
    {
      label: "Invite your team",
      title: "Email your employees a survey to track their carbon emissions.",
      input: (
        <Grid item container direction="column" spacing={2} alignItems="center">
          <Grid item style={{ width: "100%" }}>
            {newEmployees.map((employee, idx) => (
              <ManualEmployeeEmailsRow
                key={`manual-employee-email-row-${idx}`}
                employee={employee}
                employeeCount={newEmployees.length}
                editEmployee={editRowData(idx, setNewEmployees)}
                removeEmployee={removeDataRow(idx, setNewEmployees)}
                submitErrorMsg={submitErrorMsg}
                setSubmitErrorMsg={setSubmitErrorMsg}
                idx={idx}
              />
            ))}
          </Grid>
          <Grid
            item
            container
            justifyContent="space-between"
            alignItems="center"
            spacing={2}
          >
            <Grid item>
              <StyledEngineProvider injectFirst>
                <ThemeProvider theme={mainTheme}>
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={() => addDataRow(setNewEmployees, {})}
                    disabled={isStarterTierEmployeeLimitReached}
                  >
                    New Employee
                  </Button>
                </ThemeProvider>
              </StyledEngineProvider>
            </Grid>
          </Grid>
          <Box
            sx={{ mt: 4, display: "flex", justifyContent: "center", gap: 2 }}
          >
            <StyledEngineProvider injectFirst>
              <ThemeProvider theme={mainTheme}>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<ScheduleIcon />}
                  onClick={() => setShowScheduleModal(true)}
                  disabled={!areEmployeesValid()}
                >
                  Schedule Delivery
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<SendIcon />}
                  onClick={submitEmployees}
                  disabled={!areEmployeesValid()}
                >
                  Send Survey Now
                </Button>
              </ThemeProvider>
            </StyledEngineProvider>
          </Box>
          <ScheduleSurveyModal
            open={showScheduleModal}
            onClose={() => setShowScheduleModal(false)}
            onSchedule={handleScheduleSurvey}
            employeeData={newEmployees}
            companyTimezone="America/Los_Angeles"
          />
        </Grid>
      ),
      noButton: true,
      formAdvanceErrorMessage:
        isStarterTierEmployeeLimitReached &&
        `Adding ${newEmployees.length} employees will bring you above the 
      ${STARTER_TIER_MAXIMUM_NUMBER_EMPLOYEES} employee limit. Reduce
      the number of employees to email or upgrade your subscription.`,
      onAdvanceForm: submitEmployees
    }
  ];
};

const useEmployeesCsvUploader = ({ onClose }) => {
  const { activateSnackbar } = usePlatformLayoutContext();
  const processEmployeeInvites = useProcessEmployeeInvites();
  const [csvEmployeeData, setCsvEmployeeData] = useState(null);
  const [showScheduleModal, setShowScheduleModal] = useState(false);

  const triggerSnackbar = (snackbarMessage, type = "warning") =>
    activateSnackbar({ message: snackbarMessage, alert: type });

  const fieldValidator = (headers) => {
    if (!doesFieldExist(headers, "email")) {
      return createErrorObj(
        "The email field is required to invite your employees"
      );
    }
    if (
      !(
        (doesFieldExist(headers, "firstName") &&
          doesFieldExist(headers, "lastName")) ||
        doesFieldExist(headers, "fullName")
      )
    ) {
      return createErrorObj("You must select fields for your employees names");
    }

    return { success: true };
  };

  const handleProcessData = (data) => {
    setCsvEmployeeData(data);

    return processEmployeeInvites({
      onClose,
      triggerSnackbar
    })(data);
  };

  const handleScheduleSurvey = async ({
    scheduledTime,
    timezone,
    employeeData
  }) => {
    await processEmployeeInvites({
      onClose,
      triggerSnackbar,
      isScheduled: true,
      scheduledTime,
      timezone
    })({ data: employeeData });

    setShowScheduleModal(false);
  };

  const employeeCsvUploaderSteps = useCsvUploader({
    title: "Invite your team",
    knownFields,
    fieldOptions,
    docType: "employees",
    fieldValidator,
    processData: handleProcessData,
    firstStepText:
      "Upload a CSV of your employees and we'll invite them to fill out their profiles for Aclymate.",
    submitText: "Send Survey Now",
    setOpen: onClose
  });

  if (employeeCsvUploaderSteps.length > 0) {
    const lastStep =
      employeeCsvUploaderSteps[employeeCsvUploaderSteps.length - 1];
    const originalInput = lastStep.input;
    const originalOnAdvance = lastStep.onAdvanceForm;

    lastStep.input = (
      <>
        {originalInput}
        <Box sx={{ mt: 4, display: "flex", justifyContent: "center", gap: 2 }}>
          <StyledEngineProvider injectFirst>
            <ThemeProvider theme={mainTheme}>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<ScheduleIcon />}
                onClick={() => setShowScheduleModal(true)}
              >
                Schedule Delivery
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<SendIcon />}
                onClick={originalOnAdvance}
              >
                Send Survey Now
              </Button>
            </ThemeProvider>
          </StyledEngineProvider>
        </Box>
        <ScheduleSurveyModal
          open={showScheduleModal}
          onClose={() => setShowScheduleModal(false)}
          onSchedule={handleScheduleSurvey}
          employeeData={csvEmployeeData?.data}
          companyTimezone="America/Los_Angeles"
        />
      </>
    );
    lastStep.noButton = true;
  }

  return employeeCsvUploaderSteps;
};

const OptionCard = ({ option, chooseOption, isSelected, color }) => {
  const theme = useTheme();
  const { isSimulation } = usePlatformLayoutContext();

  const { isStarterTier } = useStarterTierSubscriptionFlags();

  const { title, subtitle, IconComponent, value, onClick } = option;

  const isDisabled =
    (isSimulation &&
      (option.value === "csvOrManualEmail" || option.value === "email-link")) ||
    (isStarterTier && value === "email-link");

  const borderStyleObj = isSelected ? { border: `thick solid ${color}` } : {};
  const disabledStyleObj = isDisabled
    ? { backgroundColor: theme.palette.backgroundGray.dark, opacity: 0.8 }
    : {};

  return (
    <Tooltip
      title={
        isSimulation
          ? "This feature is disabled in simulation mode"
          : "This feature is disabled in the Starter plan and simulation mode"
      }
      disableHoverListener={!isDisabled}
    >
      <div style={{ width: "100%", height: "100%" }}>
        <ButtonBase
          onClick={() => {
            if (onClick) {
              onClick();
            }
            return chooseOption(value);
          }}
          style={{ height: "100%", width: "100%" }}
          disabled={isDisabled}
        >
          <DefaultPaper
            style={{
              height: "100%",
              width: "100%",
              position: "relative",
              ...borderStyleObj,
              ...disabledStyleObj
            }}
          >
            {isSelected && (
              <div
                style={{
                  backgroundColor: color,
                  position: "absolute",
                  right: 0,
                  top: 0,
                  padding: `calc(${theme.spacing(1)} / 2)`,
                  borderRadius: "0 0 0 4px",
                  color: "white"
                }}
              >
                <DoneIcon />
              </div>
            )}
            <Typography variant="body1">{title}</Typography>
            {IconComponent && (
              <IconComponent style={{ color }} fontSize="large" />
            )}
            {subtitle && (
              <Typography
                variant="caption"
                color="textSecondary"
                display="block"
              >
                {subtitle}
              </Typography>
            )}
          </DefaultPaper>
        </ButtonBase>
      </div>
    </Tooltip>
  );
};

const EmployeeSurveyLinkAndPin = ({ employeeSurveyLinkAndPinObj, color }) => {
  const { updateAccountData } = useCachedFirebaseCrud();

  return employeeSurveyLinkAndPinObj.map(
    ({ shuffleIcon, ...otherProps }, idx) => (
      <Grid item xs={5} key={`link-and-pin-obj-${idx}`}>
        <CopyButtonCard
          {...otherProps}
          color={color}
          secondaryAction={
            shuffleIcon && (
              <Tooltip title="Shuffle">
                <IconButton
                  style={{ padding: "8px" }}
                  onClick={() =>
                    updateAccountData({
                      employeeSurveyPin: generateRandomPin()
                    })
                  }
                  size="large"
                >
                  {shuffleIcon}
                </IconButton>
              </Tooltip>
            )
          }
        />
      </Grid>
    )
  );
};

const AddingEmployeeOptions = ({
  onClose,
  selectedOption,
  setSelectedOption,
  selectedSubOption,
  setSelectedSubOption,
  employeeSurveyPin,
  isSliderLeftContent
}) => {
  const theme = useTheme();

  const { updateAccountData } = useCachedFirebaseCrud();
  const [{ employeeCountConfirmed }] = useAccountData();

  const [employeeConfirmNumber, setEmployeeConfirmNumber] = useState();

  const {
    title,
    color,
    backdropColor,
    primaryIcon: PrimaryIcon,
    options,
    subOptions
  } = useInputType("employees");

  const checkCompanySlugSetConfirmEmployeeCount = async () =>
    await updateAccountData({
      employeeCount: employeeConfirmNumber,
      employeeCountConfirmed: true,
      employeeSurveyPin: generateRandomPin()
    });

  const companyId = getAccountId();
  const employeeSurveyLinkAndPinObj = [
    {
      title: "Employee Survey Link",
      text: `https://${window.location.host}/employee-survey/${companyId}/new-employee`
    },
    {
      title: "Employee Survey Pin",
      text: employeeSurveyPin,
      shuffleIcon: <ShuffleIcon style={{ fontSize: "16px" }} />
    }
  ];

  const sliderLeftContentStyling = isSliderLeftContent && { right: "40%" };

  return (
    <Backdrop
      open
      style={{
        color: "white",
        zIndex: 1300,
        ...sliderLeftContentStyling
      }}
      onFormClose={!isSliderLeftContent && (() => onClose(false))}
      backgroundColor={backdropColor && hexToRgba(backdropColor, 0.85)}
    >
      <Box
        style={{
          height: "100%",
          padding: theme.spacing(4)
        }}
      >
        <Grid container direction="column" spacing={4} alignItems="center">
          <Grid item>
            <Avatar
              style={{
                backgroundColor: color,
                height: 100,
                width: 100
              }}
            >
              <PrimaryIcon style={{ fontSize: "4rem" }} />
            </Avatar>
          </Grid>
          <Grid item>
            <Typography variant="h5">{title}</Typography>
          </Grid>
          <Grid item>
            <Typography variant="h6">
              How would you like to add your team?
            </Typography>
          </Grid>
          <Grid container item spacing={2}>
            {options.map((option, idx) => (
              <Grid key={`input-option-${idx}`} item xs={4}>
                <OptionCard
                  chooseOption={(value) => setSelectedOption(value)}
                  option={option}
                  isSelected={option.value === selectedOption}
                  color={color}
                />
              </Grid>
            ))}
          </Grid>
          {selectedOption === "csvOrManualEmail" && (
            <Grid container direction="column" alignItems="center" spacing={2}>
              <Grid item>
                <Typography variant="h6">
                  Select an option to invite your team
                </Typography>
              </Grid>
              <Grid item container justifyContent="center" spacing={2}>
                {subOptions
                  .filter((subOption) => !subOption.displayAsLink)
                  .map((subOption, idx) => (
                    <Grid key={`input-option-${idx}`} item xs={4}>
                      <OptionCard
                        chooseOption={(value) => setSelectedSubOption(value)}
                        option={subOption}
                        isSelected={subOption.value === selectedSubOption}
                        color={color}
                      />
                    </Grid>
                  ))}
              </Grid>
              {subOptions
                .filter((subOption) => subOption.displayAsLink)
                .map((subOption, idx) => (
                  <Grid item key={`input-link-option-${idx}`}>
                    <Tooltip title={subOption.subtitle || ""}>
                      <Button
                        variant="text"
                        size="small"
                        color="inherit"
                        onClick={() => setSelectedSubOption(subOption.value)}
                      >
                        {subOption.title}
                      </Button>
                    </Tooltip>
                  </Grid>
                ))}
            </Grid>
          )}
          {selectedOption === "email-link" && (
            <>
              {!employeeCountConfirmed && (
                <Grid
                  item
                  container
                  alignItems="center"
                  justifyContent="center"
                  direction="column"
                  spacing={2}
                >
                  <Grid item style={{ width: "40%" }}>
                    <StyledEngineProvider injectFirst>
                      <ThemeProvider theme={mergeDarkTheme}>
                        <TextField
                          label="Please confirm how many employees you have"
                          value={employeeConfirmNumber}
                          setValue={setEmployeeConfirmNumber}
                        />
                      </ThemeProvider>
                    </StyledEngineProvider>
                  </Grid>
                  <Grid item>
                    <StyledEngineProvider injectFirst>
                      <ThemeProvider theme={mainTheme}>
                        <Button
                          variant="contained"
                          size="small"
                          color="secondary"
                          disabled={
                            !employeeConfirmNumber ||
                            !numbersRegExpTest(employeeConfirmNumber) ||
                            employeeConfirmNumber <= 0
                          }
                          onClick={checkCompanySlugSetConfirmEmployeeCount}
                        >
                          Generate Link And Pin
                        </Button>
                      </ThemeProvider>
                    </StyledEngineProvider>
                  </Grid>
                </Grid>
              )}
              {employeeCountConfirmed && (
                <Grid item container justifyContent="center" spacing={2}>
                  <EmployeeSurveyLinkAndPin
                    employeeSurveyLinkAndPinObj={employeeSurveyLinkAndPinObj}
                    color={color}
                  />
                </Grid>
              )}
            </>
          )}
        </Grid>
      </Box>
    </Backdrop>
  );
};

const AddManuallyForm = ({
  onClose,
  companyStartDate,
  mostRecentAccountingDate,
  setSelectedOption,
  selectedOption,
  isSliderLeftContent,
  loadingText
}) => {
  const newEmployeeForm = useNewEmployeeForm({
    onClose,
    companyStartDate,
    mostRecentAccountingDate
  });

  return (
    <>
      <Box
        style={{
          position: "fixed",
          top: 16,
          left: 16,
          zIndex: 1400
        }}
      >
        <SurveyLanguageToggle />
      </Box>
      <MultiPartFormLayout
        type="employees"
        forms={newEmployeeForm}
        setOption={setSelectedOption}
        selectedOption={selectedOption}
        onClose={!isSliderLeftContent && onClose}
        submitLoadingText={loadingText}
        displayOptions={selectedOption}
        style={isSliderLeftContent && { right: "40%" }}
      />
    </>
  );
};

const AddEmployeesInputs = ({ onClose, isSliderLeftContent }) => {
  const accountId = window.sessionStorage.getItem("accountId");
  const [{ employeeSurveyPin, startDate: companyStartDate }] = useAccountData();
  const [{ mostRecentAccountingDate }] = useAccountingData();

  const emailEmployeesForm = useManualEmployeeEmails({ onClose });
  const uploadEmployeeCsvForm = useEmployeesCsvUploader({ onClose });
  const bulkImportNoSurveyForm = useEmployeeBulkImportUploader({ onClose });

  const [selectedOption, setSelectedOption] = useState("");
  const [selectedSubOption, setSelectedSubOption] = useState("");

  const subOptionForms = {
    csv: uploadEmployeeCsvForm,
    emailManual: emailEmployeesForm,
    bulkImportNoSurvey: bulkImportNoSurveyForm
  };
  const formsLoadingText = {
    csv: "Please wait while we upload your employees...",
    add: "Please wait while we load your new employee...",
    emailManual: "Please wait while we email your new employee...",
    bulkImportNoSurvey:
      "Please wait while we import your customer-supplied employee data..."
  };

  return (
    <>
      {selectedOption === "add" ? (
        <SurveyLanguageProvider>
          <AddManuallyForm
            onClose={onClose}
            companyStartDate={companyStartDate}
            mostRecentAccountingDate={mostRecentAccountingDate}
            setSelectedOption={setSelectedOption}
            selectedOption={selectedOption}
            isSliderLeftContent={isSliderLeftContent}
            loadingText={formsLoadingText.add}
          />
        </SurveyLanguageProvider>
      ) : selectedSubOption === "csv" ||
        selectedSubOption === "emailManual" ||
        selectedSubOption === "bulkImportNoSurvey" ? (
        <MultiPartFormLayout
          type="employees"
          forms={subOptionForms[selectedSubOption]}
          setOption={setSelectedSubOption}
          selectedOption={selectedSubOption}
          onClose={!isSliderLeftContent && onClose}
          displayOptions={selectedSubOption}
          style={isSliderLeftContent && { right: "40%" }}
        />
      ) : (
        <AddingEmployeeOptions
          accountId={accountId}
          selectedOption={selectedOption}
          setSelectedOption={setSelectedOption}
          selectedSubOption={selectedSubOption}
          setSelectedSubOption={setSelectedSubOption}
          employeeSurveyPin={employeeSurveyPin}
          onClose={onClose}
          isSliderLeftContent={isSliderLeftContent}
        />
      )}
    </>
  );
};
export default AddEmployeesInputs;
